import { useEffect, useRef } from 'react';
import { DocRow } from './DocPreview';
import { DocActionButton } from './DocActionButton';
import type { DocRows } from './useDocRows';
import styles from './DocPreview.module.css';

export interface DocCustomRowsProps {
  rows: DocRows;
  /** Klíč sekce (`s01`), stejný jako u `hiddenCount` / `restoreSection`. */
  section: string;
  /** Vlastní řádky se vykreslí monospace (čísla, kódy). */
  mono?: boolean;
}

/**
 * Vlastní řádky sekce: popisek i hodnota jsou editovatelné, ✕ řádek odebere.
 * Vkládá se na konec `<DocBlock>` za standardní řádky. Do PDF se tiskne
 * jako každý jiný řádek (popisek vlevo, hodnota vpravo).
 */
export function DocCustomRows({ rows, section, mono }: DocCustomRowsProps) {
  const list = rows.customRows(section);
  if (!list.length) return null;
  return (
    <>
      {list.map((r) => (
        <DocRow
          key={r.id}
          k={
            <CustomKeyInput
              value={r.k}
              autoFocus={rows.lastAddedId === r.id}
              onChange={(k) => rows.updateCustomRow(section, r.id, { k })}
            />
          }
          mono={mono}
          value={r.v}
          onChange={(v) => rows.updateCustomRow(section, r.id, { v })}
          placeholder="hodnota"
          onRemove={() => rows.removeCustomRow(section, r.id)}
          removeTitle="Odebrat řádek"
        />
      ))}
    </>
  );
}

function CustomKeyInput({
  value,
  autoFocus,
  onChange,
}: {
  value: string;
  autoFocus: boolean;
  onChange: (next: string) => void;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (autoFocus) ref.current?.focus();
    // Jen při vzniku řádku.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <input
      ref={ref}
      type="text"
      className={styles.rowKeyInput}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Popisek"
      aria-label="Popisek řádku"
    />
  );
}

export interface DocAddRowButtonProps {
  rows: DocRows;
  section: string;
  /** Text tlačítka, výchozí „+ Řádek". */
  label?: string;
}

/** Tlačítko do `actions=` slotu `DocBlock`: přidá do sekce vlastní řádek. */
export function DocAddRowButton({ rows, section, label = '+ Řádek' }: DocAddRowButtonProps) {
  return (
    <DocActionButton onClick={() => rows.addCustomRow(section)} title="Přidat vlastní řádek">
      {label}
    </DocActionButton>
  );
}
